import { useState } from 'react';

import PreSignUp, { type PreSignUpOption } from '@/karehero-app/auth/PreSignUp';
import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import AccountLoadingScreen from '@/karehero-app/access/AccountLoadingScreen';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import WorkEmailScreen from '@/karehero-app/access/WorkEmailScreen';
import PartnerCodeScreen from '@/karehero-app/access/PartnerCodeScreen';
import PayrollIdScreen from '@/karehero-app/access/PayrollIdScreen';
import CodeScreen from '@/karehero-app/access/CodeScreen';
import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';

/**
 * Every screen of getting in, one id each, so the flow can be opened at any of
 * them rather than clicked to from the start.
 */
export type GettingInStep =
  | 'intro'
  | 'register'
  | 'signin'
  | 'creating'
  | 'access'
  | 'work-email'
  | 'partner-code'
  | 'payroll-id'
  | 'code'
  | 'verified';

/**
 * Getting in, walkable on its own.
 *
 * The screens are the master prototype's, and the walk is not: this one starts
 * at the intro and stops at You're in, which is where the project stops. The
 * master carries on into Tell Us Once, because the app does.
 *
 * A component holding its own step rather than a mount with routes, the way the
 * intake does it: the master owns the /master URLs, and a second app claiming
 * them would navigate this one out of its own frame on the first tap.
 */
const GettingIn = ({
  initial = 'intro',
  option = 'chat',
}: {
  initial?: GettingInStep;
  /** Which opening the intro shows. See PreSignUpOption. */
  option?: PreSignUpOption;
}) => {
  const [step, setStep] = useState<GettingInStep>(initial);
  const account = { onSignOut: () => setStep('intro') };

  // Behind the Kinde sheet, which slides up over whatever the app was showing.
  const welcome = (
    <WelcomeScreen
      onCreateAccount={() => setStep('register')}
      onSignIn={() => setStep('signin')}
      onForgotPassword={() => setStep('signin')}
    />
  );

  switch (step) {
    case 'intro':
      return (
        <PreSignUp
          option={option}
          onCreateAccount={() => setStep('register')}
          onSignIn={() => setStep('signin')}
        />
      );

    case 'register':
      return (
        <>
          {welcome}
          <KindeHostedSheet
            variant='register'
            onClose={() => setStep('intro')}
            onComplete={() => setStep('creating')}
          />
        </>
      );

    // Signing in is getting in too, and it skips the account being made: an
    // account that exists has its access already.
    case 'signin':
      return (
        <>
          {welcome}
          <KindeHostedSheet
            variant='sign-in'
            onClose={() => setStep('intro')}
            onComplete={() => setStep('verified')}
          />
        </>
      );

    case 'creating':
      return <AccountLoadingScreen onContinue={() => setStep('access')} />;

    case 'access':
      return (
        <FundingScreen
          onContinue={(choice) =>
            setStep(
              {
                work: 'work-email' as const,
                partner: 'partner-code' as const,
                payroll: 'payroll-id' as const,
              }[choice],
            )
          }
          onSignOut={() => setStep('intro')}
        />
      );

    case 'work-email':
      return (
        <WorkEmailScreen
          account={account}
          onSubmit={() => setStep('code')}
          onBack={() => setStep('access')}
        />
      );

    case 'partner-code':
      return (
        <PartnerCodeScreen
          account={account}
          onSubmit={() => setStep('verified')}
          onBack={() => setStep('access')}
        />
      );

    case 'payroll-id':
      return (
        <PayrollIdScreen
          account={account}
          onSubmit={() => setStep('verified')}
          onBack={() => setStep('access')}
        />
      );

    case 'code':
      return (
        <CodeScreen
          account={account}
          onVerify={() => setStep('verified')}
          onUsePayrollId={() => setStep('payroll-id')}
          onSignIn={() => setStep('signin')}
          onBack={() => setStep('work-email')}
        />
      );

    // The end of this project. Continue goes back to the front rather than
    // nowhere: what happens next is Tell Us Once, which is its own prototype,
    // and the master prototype is where the two run together.
    case 'verified':
      return <AccessVerifiedScreen onContinue={() => setStep('intro')} />;
  }
};

export default GettingIn;
